import * as R from "ramda";

import { toDecimal, toFractionRound } from "../../utils";
import {
  INSERT_CONTOUR_SEMI_CERISE,
  INSERT_CONTOUR_SILICONE,
  INSERT_JOPO_OVAL,
  INSERT_JOPO_OVAL_DOTS,
  INSERT_JOPO_POWER_FLAT,
  INSERT_JOPO_POWER_LIFT,
  INSERT_JOPO_POWER_OVAL,
  INSERT_TURBO_MISS_QUAD_OVAL,
  INSERT_TURBO_MISS_QUAD_POWER_OVAL,
  INSERT_TURBO_QUAD_2_OVAL_NUBS,
  INSERT_TURBO_QUAD_2_SEMI,
  INSERT_TURBO_QUAD_CLASSIC_OVAL,
  INSERT_TURBO_QUAD_CLASSIC_POWER_LIFT,
  INSERT_TURBO_QUAD_CLASSIC_PRO_OVAL,
  INSERT_TURBO_QUAD_CLASSIC_PRO_POWER_LIFT,
  INSERT_TURBO_QUAD_OVAL,
  INSERT_TURBO_QUAD_OVAL_MESH,
  INSERT_TURBO_QUAD_POWER_OVAL,
  INSERT_TURBO_QUAD_SB_POWER_LIFT,
  INSERT_TURBO_QUAD_SB_SEMI,
  INSERT_ULTIMATE_TLO_LIFT,
  INSERT_ULTIMATE_TLO_LIFT_7_8,
  INSERT_ULTIMATE_TLO_OVAL,
  INSERT_ULTIMATE_TLO_OVAL_7_8,
  INSERT_VISE_OVAL,
  INSERT_VISE_OVAL_7_8,
  INSERT_VISE_POWER_LIFT,
  INSERT_VISE_POWER_LIFT_7_8,
  INSERT_VISE_POWER_OVAL,
  INSERT_VISE_POWER_OVAL_7_8,
  INSERT_VISE_SEMI,
  INSERT_VISE_SEMI_7_8,
  INSERT_TYPE_BLANK,
} from "../db/datafixtures/idx_data_insert_types";

/**
 * The measuring rules from the pro shop aide-mémoire, as arithmetic.
 *
 * Everything the app already knows stays where it is: the thumb pitch table
 * lives in calculateFlexPitch(), the cut-to-cut in getAutoCutToCut(), and the
 * grip constants in createSpecsheet(). What is here is the part that until
 * now only existed on paper and in the head of whoever was teaching.
 *
 * No React, no redux, no datasource lookups of its own -- every function is
 * handed the option list it is allowed to land on, because a fraction the
 * shop's pickers do not offer is not a usable answer.
 */

// A fraction the pickers never offer.
const BLANK = "---";

const usable = (dataset) =>
  (dataset || []).filter(
    (entry) => entry && entry.fraction !== BLANK && entry.fraction !== ""
  );

/**
 * The closest fraction the shop's own picker offers.
 *
 * A shop set to sixteenths must not be handed 5/32 for a value it cannot
 * store, so every proposal this module makes lands on a real option first.
 */
export const snapToDataset = (decimal, dataset) => {
  const options = usable(dataset);
  if (options.length === 0 || !Number.isFinite(decimal)) return "";

  return options.reduce((best, entry) =>
    Math.abs(entry.decimal - decimal) < Math.abs(best.decimal - decimal)
      ? entry
      : best
  ).fraction;
};

/** "+1/16", "−1/8", "0" -- for showing a difference, never for storing one. */
export const formatSigned = (decimal, round = 64) => {
  if (!Number.isFinite(decimal) || Math.abs(decimal) < 1 / (round * 2)) {
    return "0";
  }

  const size = toFractionRound(Math.abs(decimal), round);
  return decimal < 0 ? `−${size}` : `+${size}`;
};

/* ------------------------------------------------------------------ *
 * Ring finger span
 * ------------------------------------------------------------------ */

export const CONVENTIONAL_RING_DIFFERENCE = 1 / 8;
export const FIVE_SIXTEENTHS = 5 / 16;

/** Conventional: the ring finger is always +1/8 of the middle finger span. */
export const conventionalRingSpan = (
  middleSpan,
  spanMeasurement,
  spanDataset
) => {
  if (!middleSpan) return "";

  return snapToDataset(
    toDecimal(middleSpan, spanMeasurement) + CONVENTIONAL_RING_DIFFERENCE,
    spanDataset
  );
};

/**
 * Fingertip: the 5/16 rule -- the one that gets forgotten most.
 *
 * X is the gap measured between the ring finger's first joint and the middle
 * finger's line carried across. The ring finger's difference is 5/16 − X, so
 * a small X makes the ring finger longer, not shorter.
 *
 * The same arithmetic runs inside ModalSpecsheets' F2SpanSelector, but only
 * when the shop turned on the ring-finger difference display. Teaching the
 * rule cannot depend on a display setting, so the wizard carries it too.
 */
export const ringSpanFromMeasuredX = (
  middleSpan,
  measuredX,
  spanMeasurement,
  spanDataset
) => {
  const difference = FIVE_SIXTEENTHS - toDecimal(measuredX, spanMeasurement);
  const span = middleSpan
    ? snapToDataset(
        toDecimal(middleSpan, spanMeasurement) + difference,
        spanDataset
      )
    : "";

  return { difference, differenceLabel: formatSigned(difference), span };
};

/* ------------------------------------------------------------------ *
 * Cut to cut
 * ------------------------------------------------------------------ */

/**
 * How wide the insert is, in the shop's own denominator.
 *
 * "No inserts" is a real answer here and its width is zero, which is why the
 * lookup returns 0 rather than nothing when the size has not been picked yet
 * for an insert that has no width to give.
 */
export const insertWidth = (
  gripTypeName,
  size,
  insertTypes,
  insertSizes,
  spanMeasurement,
  round,
) => {
  const type = R.find(R.propEq("type", gripTypeName || ""))(insertTypes || []);
  if (!type) return null;

  const entry = R.find(
    (item) => item.type_id === type.id && item.size === size,
  )(insertSizes || []);

  if (!entry) return type.id === INSERT_TYPE_BLANK ? 0 : null;

  return toDecimal(
    toFractionRound(entry.widthDecimal || 0, round),
    spanMeasurement,
  );
};

/**
 * The full span less the insert, landing on the option below it.
 *
 * Rounding down rather than to the nearest is deliberate: it is what the
 * sheet's own auto cut to cut does, and a cut that comes out long is a hole
 * drilled too far apart.
 */
export const cutToCutSpan = (spanFull, width, spanMeasurement, spanDataset) => {
  if (!spanFull || width === null || width === undefined) return "";

  const target = toDecimal(spanFull, spanMeasurement) - width;
  const options = usable(spanDataset);

  const below = options.filter((entry) => entry.decimal <= target);
  if (below.length === 0) return "";

  return below.reduce((best, entry) =>
    entry.decimal > best.decimal ? entry : best,
  ).fraction;
};

/* ------------------------------------------------------------------ *
 * Insert compensation -- adding outer angle
 * ------------------------------------------------------------------ */

export const INSERT_COMPENSATION_GROUPS = [
  {
    id: "oval",
    amount: 0,
    family: "Oval / Perfect Oval",
    inserts: [
      INSERT_TURBO_QUAD_OVAL,
      INSERT_TURBO_QUAD_OVAL_MESH,
      INSERT_TURBO_QUAD_2_OVAL_NUBS,
      INSERT_TURBO_QUAD_CLASSIC_OVAL,
      INSERT_TURBO_QUAD_CLASSIC_PRO_OVAL,
      INSERT_TURBO_MISS_QUAD_OVAL,
      INSERT_CONTOUR_SILICONE,
      INSERT_VISE_OVAL,
      INSERT_VISE_OVAL_7_8,
      INSERT_ULTIMATE_TLO_OVAL,
      INSERT_ULTIMATE_TLO_OVAL_7_8,
      INSERT_JOPO_OVAL,
      INSERT_JOPO_OVAL_DOTS,
    ],
  },
  {
    id: "power_oval",
    amount: 1 / 8,
    family: "Power Oval",
    inserts: [
      INSERT_TURBO_QUAD_POWER_OVAL,
      INSERT_TURBO_MISS_QUAD_POWER_OVAL,
      INSERT_VISE_POWER_OVAL,
      INSERT_VISE_POWER_OVAL_7_8,
      INSERT_JOPO_POWER_OVAL,
    ],
  },
  {
    id: "semi",
    amount: 3 / 16,
    family: "Semi",
    inserts: [
      INSERT_TURBO_QUAD_2_SEMI,
      INSERT_TURBO_QUAD_SB_SEMI,
      INSERT_CONTOUR_SEMI_CERISE,
      INSERT_VISE_SEMI,
      INSERT_VISE_SEMI_7_8,
    ],
  },
  {
    id: "power_lift",
    amount: 1 / 4,
    family: "Power Regular / PowerLift / Power Flat",
    inserts: [
      INSERT_TURBO_QUAD_CLASSIC_POWER_LIFT,
      INSERT_TURBO_QUAD_CLASSIC_PRO_POWER_LIFT,
      INSERT_TURBO_QUAD_SB_POWER_LIFT,
      INSERT_VISE_POWER_LIFT,
      INSERT_VISE_POWER_LIFT_7_8,
      INSERT_ULTIMATE_TLO_LIFT,
      INSERT_ULTIMATE_TLO_LIFT_7_8,
      INSERT_JOPO_POWER_LIFT,
      INSERT_JOPO_POWER_FLAT,
    ],
  },
];

// Order matters: "Power Oval" has to be read before "Oval", and a lift before
// anything else, or the looser pattern swallows the tighter one.
const FAMILY_PATTERNS = [
  { pattern: /power\s*(lift|flat)/i, id: "power_lift" },
  { pattern: /semi/i, id: "semi" },
  { pattern: /power\s*oval/i, id: "power_oval" },
  { pattern: /oval/i, id: "oval" },
];

/**
 * How much outer angle a given insert asks for.
 *
 * Keyed by the catalogue's own ids so a renamed insert keeps its answer, with
 * a fallback on the name for inserts added after this was written. An insert
 * nobody has classified returns null: the wizard then says it has no
 * recommendation rather than inventing one.
 */
export const insertCompensation = (gripTypeName, insertTypes) => {
  if (!gripTypeName) return null;

  const entry = R.find(R.propEq("type", gripTypeName))(insertTypes || []);
  const byId =
    entry &&
    INSERT_COMPENSATION_GROUPS.find((group) =>
      group.inserts.includes(entry.id)
    );

  if (byId) return byId;

  const match = FAMILY_PATTERNS.find(({ pattern }) =>
    pattern.test(gripTypeName)
  );

  return match
    ? INSERT_COMPENSATION_GROUPS.find((group) => group.id === match.id)
    : null;
};

/** The lateral with the insert's outer angle added, snapped to the picker. */
export const withCompensation = (
  lateral,
  amount,
  pitchMeasurement,
  pitchDataset
) => {
  if (!amount) return lateral || "";

  return snapToDataset(
    toDecimal(lateral || "0", pitchMeasurement) + amount,
    pitchDataset
  );
};

/* ------------------------------------------------------------------ *
 * Finger pitch from first-joint flexibility
 * ------------------------------------------------------------------ */

export const FINGER_FLEX_TABLE = [
  { degrees: "70", low: 0, high: 0, zero: true },
  { degrees: "45", low: 1 / 4, high: 3 / 8 },
  { degrees: "30", low: 1 / 2, high: 3 / 4 },
];

/**
 * The pitch the fingers want, read off the first joint.
 *
 * The table gives a range and the shop drills what it sees, so the middle is
 * offered and the range stays on screen. Everything here is reverse pitch:
 * over 80% of customers get reverse only.
 */
export const fingerPitchFromFlex = (degrees, pitchMeasurement, pitchDataset) => {
  const row = FINGER_FLEX_TABLE.find((entry) => entry.degrees === degrees);
  if (!row) return null;

  const middle = (row.low + row.high) / 2;

  return {
    ...row,
    middle,
    lowLabel: toFractionRound(row.low, 64),
    highLabel: toFractionRound(row.high, 64),
    reverse: row.zero ? "0" : snapToDataset(middle, pitchDataset),
  };
};

/* ------------------------------------------------------------------ *
 * Thumb pitch adjustments
 * ------------------------------------------------------------------ */

// Each one is reverse pitch, positive meaning "more reverse". The table gives
// 1/16 to 1/8; the middle is offered for the same reason as above.
export const THUMB_ADJUSTMENTS = [
  { id: "long_thumb", reverse: 3 / 32, note: "over 2 5/8 in" },
  { id: "short_thumb", reverse: -3 / 32, note: "under 2 in" },
  { id: "moist_skin", reverse: 3 / 32 },
  { id: "dry_skin", reverse: -3 / 32 },
];

/**
 * Move a thumb pitch by a reverse amount, keeping the sheet's convention.
 *
 * A pitch lives in two fields and only ever one of them is filled, so a
 * forward pitch pushed far enough into reverse has to change fields rather
 * than go negative.
 */
export const shiftThumbPitch = (
  { thm_for, thm_rev },
  reverseAmount,
  pitchMeasurement,
  pitchDataset
) => {
  const forward = toDecimal(thm_for || "0", pitchMeasurement);
  const reverse = toDecimal(thm_rev || "0", pitchMeasurement);
  const net = forward - reverse - reverseAmount;

  if (Math.abs(net) < 1 / 128) return { thm_for: "0", thm_rev: "" };

  return net > 0
    ? { thm_for: snapToDataset(net, pitchDataset), thm_rev: "" }
    : { thm_for: "", thm_rev: snapToDataset(-net, pitchDataset) };
};

/** The reverse amount asked for by the switches the operator turned on. */
export const thumbAdjustmentTotal = (selected = {}) =>
  THUMB_ADJUSTMENTS.reduce(
    (total, adjustment) =>
      selected[adjustment.id] ? total + adjustment.reverse : total,
    0
  );
